import React from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';

type ShellProps = {
  open: boolean;
  onClose: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  /** Header gradient stops (the same pair the section tile uses). */
  colors: [string, string];
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  /** Sticky action bar rendered under the scrolling body. */
  footer?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * Shared "flashy" form dialog chrome: glossy gradient header with icon tile, scrolling body on a soft
 * tinted background, and a sticky footer for the actions. Used by the payment and grocery forms.
 */
const FormDialogShell = ({
  open, onClose, icon, title, subtitle, colors, maxWidth = 'sm', footer, children,
}: ShellProps) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={maxWidth}
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: '28px',
        overflow: 'hidden',
        boxShadow: '0 40px 100px rgba(46,15,94,0.45)',
        maxHeight: 'calc(100% - 32px)',
        display: 'flex',
        flexDirection: 'column',
      },
    }}
  >
    <Box
      sx={{
        position: 'relative', overflow: 'hidden', color: '#fff', flexShrink: 0,
        px: { xs: 2, md: 3 }, py: 2.25,
        background: `linear-gradient(120deg, ${colors[0]} 0%, ${colors[1]} 100%)`,
        transition: 'background .35s ease',
      }}
    >
      <Box sx={{ position: 'absolute', width: 220, height: 220, right: -40, top: -110, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,.55), transparent 65%)' }} />
      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,255,255,.28), rgba(255,255,255,0) 55%)' }} />
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.75 }}>
        <Box
          sx={{
            width: 52, height: 52, borderRadius: '18px', display: 'grid', placeItems: 'center', flexShrink: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,.5), rgba(255,255,255,.15))',
            border: '1px solid rgba(255,255,255,.6)', boxShadow: '0 10px 22px rgba(0,0,0,.2)',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.15, textShadow: '0 2px 10px rgba(0,0,0,.25)' }}>
            {title}
          </Typography>
          {subtitle && <Typography variant="body2" sx={{ opacity: 0.92 }}>{subtitle}</Typography>}
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.2)', border: '1px solid rgba(255,255,255,.4)', '&:hover': { bgcolor: 'rgba(255,255,255,.32)' } }}
        >
          <CloseRounded />
        </IconButton>
      </Box>
    </Box>

    <Box
      sx={{
        flex: 1, overflowY: 'auto', p: { xs: 2, md: 3 },
        background: (t) => (t.palette.mode === 'light'
          ? `linear-gradient(180deg, ${colors[0]}14, #FAF7FF 40%)`
          : `linear-gradient(180deg, ${colors[0]}22, transparent 40%)`),
      }}
    >
      {children}
    </Box>

    {footer && (
      <Box
        sx={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
          px: { xs: 2, md: 3 }, py: 1.75,
          borderTop: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: '0 -10px 24px rgba(98,38,167,0.07)',
        }}
      >
        {footer}
      </Box>
    )}
  </Dialog>
);

/** Raised white card with a coloured icon tile + label, used to group fields. */
export const FieldCard = ({
  icon: Icon, label, color, children, sx,
}: {
  icon?: React.ComponentType<any>;
  label?: string;
  color: string;
  children: React.ReactNode;
  sx?: object;
}) => (
  <Box
    sx={{
      p: 1.75, borderRadius: '18px', bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      boxShadow: '0 6px 16px rgba(98,38,167,0.07)',
      ...sx,
    }}
  >
    {label && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        {Icon && (
          <Box
            sx={{
              width: 28, height: 28, borderRadius: '10px', display: 'grid', placeItems: 'center', color: '#fff',
              background: `linear-gradient(135deg, ${color}, ${color}B3)`,
              boxShadow: `0 5px 12px ${color}55, inset 0 1px 0 rgba(255,255,255,.4)`,
            }}
          >
            <Icon sx={{ fontSize: 16 }} />
          </Box>
        )}
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{label}</Typography>
      </Box>
    )}
    {children}
  </Box>
);

/** Primary footer button: glossy section gradient. */
export const gradientButtonSx = (colors: [string, string]) => ({
  color: '#fff',
  px: 3,
  py: 1.1,
  borderRadius: '999px',
  fontWeight: 800,
  background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
  boxShadow: `0 10px 22px ${colors[0]}66, inset 0 1px 0 rgba(255,255,255,.4)`,
  '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 14px 28px ${colors[0]}88, inset 0 1px 0 rgba(255,255,255,.4)` },
  '&.Mui-disabled': { color: '#fff', opacity: 0.6 },
});

export default FormDialogShell;
